import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Crown } from "lucide-react";
import api, { money } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const CardBadges = ({ item }) => (
  <div className="absolute right-5 top-5 flex items-center gap-2">
    <span className="rounded-full bg-white/90 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-ink backdrop-blur">
      {item.category}
    </span>
    <span className="flex items-center gap-1.5 rounded-full bg-emerald-500/90 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-white backdrop-blur">
      <span className="h-1.5 w-1.5 rounded-full bg-white" /> Available
    </span>
  </div>
);

const PriceLine = ({ item }) => (
  <p className="mt-2 font-display text-base font-bold text-white">
    {item.billing === "call" ? "Call for pricing" : (
      <>
        {money(item.billing === "tiered" ? (item.week_prices?.[0] || 0) : item.billing === "weekly" ? item.weekly_rate : item.daily_rate)}
        <span className="text-sm font-medium text-white/60"> / {item.billing === "weekly" || item.billing === "tiered" ? "week" : "day"}</span>
      </>
    )}
  </p>
);

const TierList = ({ item, highlightWeek = null }) => {
  const prices = item.week_prices || [];
  if (item.billing !== "tiered" || prices.length === 0) return <PriceLine item={item} />;
  return (
    <div
      data-testid={`tier-list-${item.id}`}
      className="mt-3 grid max-w-lg grid-cols-1 gap-y-1.5 sm:grid-cols-2 sm:gap-x-6"
    >
      {prices.map((p, i) => {
        const week = i + 1;
        const hot = highlightWeek === week;
        return (
          <div
            key={week}
            data-testid={`tier-option-${item.id}-${week}`}
            className={`flex items-center justify-between gap-3 whitespace-nowrap rounded-lg px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] ${
              hot ? "bg-gold text-ink" : "bg-white/10 text-white/80 backdrop-blur-sm"
            }`}
          >
            <span>{week} week{week > 1 ? "s" : ""}{hot && <span className="hidden sm:inline"> · best pick</span>}</span>
            <span className={`shrink-0 font-display text-sm font-extrabold normal-case tracking-normal ${hot ? "text-ink" : "text-white"}`}>
              {money(p)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const FeaturedGrid = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/listings?featured=true").then((r) => {
      const list = r.data.slice(0, 5);
      list.sort((a, b) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0));
      setItems(list);
    }).catch(() => {});
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="bg-paper py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-royal">
              Rental Packages
            </p>
            <h2 className="mt-4 font-display text-2xl font-bold leading-tight tracking-tight text-ink sm:text-3xl lg:text-4xl">
              Pick your package.
            </h2>
          </div>
          <Link
            to="/catalog"
            data-testid="featured-view-all"
            className="group flex items-center gap-2 text-sm font-semibold text-royal transition-colors duration-200 hover:text-royal-dark"
          >
            View full catalogue
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6" data-testid="featured-grid">
          {items.map((item, i) => (
            <Reveal key={item.id} delay={i * 0.08}>
              <Link
                to={`/listing/${item.id}`}
                data-testid={`listing-card-${item.id}`}
                className={`group relative block h-[480px] overflow-hidden rounded-3xl lg:h-[420px] ${item.popular ? "ring-2 ring-gold/70" : ""}`}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent" />
                {item.popular && (
                  <div
                    data-testid="popular-banner"
                    className="absolute left-0 top-6 flex items-center gap-2 rounded-r-full bg-gold py-2 pl-5 pr-6 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-ink shadow-lg shadow-gold/30"
                  >
                    <Crown className="h-4 w-4" /> Most Popular
                  </div>
                )}
                <CardBadges item={item} />
                <div className="absolute inset-x-5 bottom-5 flex items-end justify-between gap-4 sm:inset-x-8 sm:bottom-8">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                      {item.brand} — {item.location}
                    </p>
                    <h3 className="mt-1 font-display text-xl font-bold leading-snug text-white sm:text-2xl lg:text-3xl">
                      {item.title}
                    </h3>
                    {item.popular && (
                      <span
                        data-testid="popular-best-pick-mobile"
                        className="mt-2 inline-flex items-center gap-1.5 self-start rounded-full border border-gold/60 bg-ink/40 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-gold backdrop-blur-sm sm:hidden"
                      >
                        <Crown className="h-3 w-3" /> Best pick — 2-week term
                      </span>
                    )}
                    <TierList item={item} highlightWeek={item.popular ? 2 : null} />
                  </div>
                  <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-full transition-all duration-300 group-hover:scale-110 ${item.popular ? "bg-gold text-ink group-hover:shadow-lg group-hover:shadow-gold/40" : "bg-royal text-white group-hover:shadow-lg group-hover:shadow-royal/40"}`}>
                    <ArrowUpRight className="h-5 w-5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
